import type { WorkspaceDataStatus } from "@adpilot/shared";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { DataFreshnessBar } from "./DataFreshnessBar";

interface ShopDataHistoryProps {
  shop: WorkspaceDataStatus["shops"][number];
  shopName: string;
  unavailable?: boolean;
}

export function ShopDataHistory({ shop, shopName, unavailable = false }: ShopDataHistoryProps) {
  const { t, i18n } = useTranslation("settings");
  const [primary, setPrimary] = useState<"ads" | "sales">("ads");
  const reduce = useReducedMotion();
  const secondary = primary === "ads" ? "sales" : "ads";
  const name = (kind: "ads" | "sales") =>
    t(kind === "ads" ? "dataStatus.adsReports" : "dataStatus.salesReports");
  const source = shop.sources.find((item) => item.source === primary);
  const daysFor = (kind: "ads" | "sales") => {
    const item = shop.sources.find((entry) => entry.source === kind);
    const hidden =
      unavailable ||
      item?.state === "action_required" ||
      item?.state === "paused" ||
      item?.state === "unavailable";
    return (item?.reportQuality?.days.slice(-60) ?? []).map((day) =>
      hidden ? { ...day, status: "unknown" as const, updatedAt: null } : day,
    );
  };
  const primaryDays = daysFor(primary);
  const secondaryDays = daysFor(secondary);
  const displayDays = primaryDays.length
    ? primaryDays
    : secondaryDays.map((day) => ({ ...day, status: "unknown" as const, updatedAt: null }));
  const blocked =
    unavailable ||
    source?.state === "action_required" ||
    source?.state === "paused" ||
    source?.state === "unavailable";
  const complete = primaryDays.length === 60 && primaryDays.every((day) => day.status === "synced");
  const pending = primaryDays.some((day) => day.status === "pending" || day.status === "attention");
  const labelKey = blocked
    ? `dataStatus.sourceShort.${unavailable ? "unavailable" : source?.state}`
    : complete
      ? "dataStatus.shopReady"
      : pending
        ? "dataStatus.shopPending"
        : "dataStatus.shopUnknown";
  const updated = [...primaryDays].reverse().find((day) => day.status === "synced")?.updatedAt;
  return (
    <div className="flex max-w-[740px] flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ink">{name(primary)}</span>
          <Switch
            checked={primary === "sales"}
            onCheckedChange={(checked) => setPrimary(checked ? "sales" : "ads")}
            aria-label={`${name("ads")} / ${name("sales")}`}
            title={name(secondary)}
          />
        </div>
        <span className="text-muted-foreground">
          {t(labelKey)}
          {updated && !blocked
            ? ` · ${t("dataStatus.updatedAt", { time: new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(updated)) })}`
            : ""}
        </span>
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={primary}
          initial={reduce ? { opacity: 0 } : { opacity: 0, filter: "blur(4px)", y: 4 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, filter: "blur(4px)", y: -4 }}
          transition={{ duration: reduce ? 0 : 0.16 }}
        >
          <DataFreshnessBar
            days={displayDays}
            secondaryDays={secondaryDays}
            secondaryName={name(secondary)}
            shopName={shopName}
            sourceName={name(primary)}
          />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
